// Modules
import {
    addYears,
    parseISO,
    format,
    formatISO,
    addHours,
    setDate,
    isBefore,
    getDate,
    addMonths,
    subMonths,
    formatDistanceToNow,
    Locale
} from "date-fns";
import { FREQ_YEARLY } from "./constants";

export type SupportedLang = "en" | "de";

/**
 * Returns the date as a string in a specific format or by default dd/MM/YYYY.
 *
 * @param {String | Date} date the date to format
 * @param {Object} dateFormatOptions the date to format options
 * @param {String} locale the locale
 * @returns {String} date in the format dd/MM/YYYY
 */
export function formatDate(
    date: string | Date,
    dateFormatOptions: Intl.DateTimeFormatOptions | undefined = undefined,
    locale: string = "en-GB",
    forceDETimezone = false
): string {
    const germanTimezone = "Europe/Berlin";

    const dateOptions: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        ...dateFormatOptions,
        ...(forceDETimezone ? { timeZone: germanTimezone } : {})
    };

    const dateDate = new Date(date);
    const dateStr = dateDate.toLocaleDateString(locale, dateOptions);

    return dateStr;
}

/**
 * Format a date string in the "yyyy-MM-dd" format by parsing and reformatting it.
 *
 * @param {string} date - The input date string to be formatted.
 * @returns {string} - The formatted date string in "yyyy-MM-dd" format.
 */

export function formatDateReverse(date: string) {
    const parsedDate = parseISO(date);
    const formattedDate = format(parsedDate, "yyyy-MM-dd");
    return formattedDate;
}

/**
 * Returns the time as a string in a specific format or by default HH:mm.
 *
 * @param {String | Date} date the date to format
 * @param {Object} timeFormatOptions the time to format options
 * @param {String} locale the locale
 * @returns {String} time in the format HH:mm
 */
export function formatTime(
    date: string | Date,
    timeFormatOptions: Intl.DateTimeFormatOptions | undefined = undefined,
    locale: Intl.LocalesArgument | undefined = "en-GB",
    forceDETimezone = false
) {
    const germanTimezone = "Europe/Berlin";

    const timeOptions: Intl.DateTimeFormatOptions = {
        hour12: false,
        hour: "numeric",
        minute: "numeric",
        ...timeFormatOptions,
        ...(forceDETimezone ? { timeZone: germanTimezone } : {})
    };

    const dateDate = new Date(date);
    const timeStr = timeOptions.hour && dateDate.toLocaleTimeString(locale, timeOptions);

    return timeStr;
}

/**
 * Returns the time as a string in a specific format or by default hh:mm:ss.
 *
 * @param {String} date the date to format
 * @returns {String} time in the format hh:mm:ss
 */
export function createDurationTimeFromDate(date: string) {
    const time = new Date(date);
    const { minutes, seconds } = {
        minutes: time.getMinutes().toString(),
        seconds: time.getSeconds().toString()
    };
    const formattedTimeArr = [minutes, seconds].map(val => val.toString().padStart(2, "0"));
    return formattedTimeArr.join(":");
}

/**
 * Returns the date as a string in a specific format or by default dd/MM/YYYY HH:mm.
 *
 * @param {String} date the date to format
 * @param {Object} dateFormatOptions the date to format options
 * @param {Object} timeFormatOptions the time to format options
 * @param {String} locale the locale
 * @returns {String} timestamp in the format dd/MM/YYYY HH:mm
 */
export function formatTimestamp(
    date: string | Date,
    dateFormatOptions: Intl.DateTimeFormatOptions | undefined = undefined,
    timeFormatOptions: Intl.DateTimeFormatOptions | undefined = undefined,
    locale?: string
) {
    const dateStr = formatDate(date, dateFormatOptions, locale);
    const timeStr = formatTime(date, timeFormatOptions, locale);

    return `${dateStr}${timeStr ? ` ${timeStr}` : ""}`;
}

/**
 * Returns the time as a string in a specific format or by default hh:mm:ss.
 *
 * @param {String} date the date to format
 * @returns {String} time in the format hh:mm:ss
 */
export function createDateFromDurationTime(durationTime) {
    if (!durationTime) {
        return null;
    }
    const [minutes, seconds] = durationTime.split(":");

    const dateObj = new Date();
    dateObj.setMinutes(minutes);
    dateObj.setSeconds(seconds);

    const dateToISO = dateObj.toISOString();
    return dateToISO;
}

/**
 * Calculates the days of difference between two dates.
 *
 * @param {Date} dateTo the first date to compare
 * @param {Date} dateFrom the second date to compare
 * @returns the days of difference
 */
export function getDaysDiff(dateTo, dateFrom, checkUpcoming = false) {
    if (checkUpcoming && dateTo <= dateFrom) {
        return 0;
    }

    const diffInMs = 1000 * 60 * 60 * 24;
    const diffInDays = Math.round(Math.abs((dateTo - dateFrom) / diffInMs));
    return diffInDays;
}

/**
 * Calculates the months of difference between two dates.
 *
 * @param {Date} dateTo the first date to compare
 * @param {Date} dateFrom the second date to compare
 * @returns the months of difference
 */
export function getMonthDiff(dateTo, dateFrom) {
    const milisecondsToMonths = (1000 * 60 * 60 * 24 * 365) / 12;
    const diff = Math.ceil((dateTo - dateFrom) / milisecondsToMonths);

    return diff > 0 ? diff : 0;
}

/**
 * Calculates the months of difference between two dates.
 *
 * @param {Date} dateTo the first date to compare
 * @param {Date} dateFrom the second date to compare
 * @returns the months of difference
 */
export function getMonthDiffFromYears(dateTo, dateFrom) {
    const startDate = new Date(dateFrom);
    const endDate = new Date(dateTo);

    // Normalize both dates to remove time component
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    const startMonth = startDate.getMonth();
    const endMonth = endDate.getMonth();

    const monthDiff = (endYear - startYear) * 12 + (endMonth - startMonth);
    return monthDiff;
}

/**
 * Calculates the years and months (from 1 to 12) of difference between two dates.
 *
 * @param {Date} dateTo the first date to compare
 * @param {Date} dateFrom the second date to compare
 * @returns {Object} with the years and months (from 1 to 12) difference
 */
export function getDiffYearsMonths(dateTo, dateFrom) {
    if (!dateTo || !dateFrom) {
        return null;
    }

    const diffMonthsTotal = getMonthDiff(dateTo, dateFrom);

    const diffYears = diffMonthsTotal > 0 ? Math.trunc((diffMonthsTotal - 1) / 12) : 0;
    const diffMonths = diffMonthsTotal - diffYears * 12;

    return {
        years: diffYears,
        months: diffMonths
    };
}

/**
 * Get the date that is one year minus one month after the provided date,
 * with an optional adjustment to a specific payment day.
 *
 * After a full year of subscription, the next subscription cycle will start
 * one month before the anniversary date of the original subscription.
 *
 * @param {string} date - The input date in "yyyy-MM-dd" format.
 * @param {number} [paymentDay] - Optional: The specific day of the month for the payment (1-31).
 * @returns {string} - The calculated date in the "yyyy-MM-dd" format.
 */
export function getNextYearFromDate(date, paymentDay) {
    // Parse the input date
    const initialDate = parseISO(date);

    // Calculate one year minus one month
    const nextYearDate = subMonths(addYears(initialDate, 1), 1);

    // If a payment day is provided, set the day of the month
    const finalDate = paymentDay ? setDate(nextYearDate, paymentDay) : nextYearDate;

    // Return the formatted date string
    return format(finalDate, "yyyy-MM-dd");
}

/**
 * Converts a date string into ISO format.
 *
 * @param {String} dateStr the date to convert
 * @returns {Date} the date in iso format
 */
export function convertToIsoDate(dateStr) {
    const date = new Date(dateStr);

    return new Date(new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString());
}

/**
 * Formats a date object or a date string into ISO 8601 format, adjusting it to UTC time.
 *
 * @param {Object} options an object containing optional properties
 * @param {Date | String} [options.date] the date object or string to format (optional)
 * @returns {String} the date in ISO 8601 format or null if invalid input
 */
export function getUTCDate({ date }: { date?: Date | string } = {}) {
    const currentDate = date ? new Date(date) : new Date();
    const utcDate = addHours(currentDate, -currentDate.getTimezoneOffset() / 60);
    return formatISO(utcDate, { representation: "complete" }).slice(0, 19);
}

/**
 * Checks if a date is a valid date object.
 *
 * @param {Date | null} date - The date to validate.
 * @returns {boolean} True if the date is valid, false otherwise.
 */
export function isValidDate(date) {
    return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Calculates the subscription start date based on the provided plan and date.
 *
 * - For monthly plans, returns the 10th of the same month in the next year.
 * - For other plans, sets the date to the 10th of the provided month.
 *
 * If the calculated date is before today:
 *   - Adjusts to the next available 10th.
 *   - If today's date is after the 10th, shifts to the 10th of the next month.
 *
 * Returns the formatted date as "yyyy-MM-dd".
 *
 * @param {string} date - Initial date for the subscription.
 * @param {string} plan - Subscription plan type.
 * @returns {string} - Formatted start date.
 */
export function getSubscriptionStartDate(date, plan) {
    const now = new Date();
    // Payment starting day should be allways 10 of each month
    const paymentDay = 10;
    if (plan === FREQ_YEARLY) {
        return getNextYearFromDate(date, paymentDay);
    }

    let paymentDate = setDate(new Date(date), paymentDay);

    if (isBefore(paymentDate, now)) {
        paymentDate = setDate(now, paymentDay);

        // If today's date is after the 10th, shift the payment date to the next month
        if (getDate(now) > paymentDay) {
            paymentDate = addMonths(paymentDate, 1);
        }
    }

    return format(paymentDate, "yyyy-MM-dd");
}

/**
 * Returns a minimal custom `Locale` object used by `date-fns` to format relative time (e.g. "3h", "2d")
 * in a short, language-specific format for supported languages ("de", "en").
 *
 * This is used to generate compact, user-friendly timestamps like "5min" or "2h" instead of full phrases.
 *
 * @param lang - Language code (defaults to "de").
 * @returns Partial `Locale` object with customized `formatDistance` logic.
 */

const getShortTimeLocale = (lang: SupportedLang = "de"): Locale => {
    const map: Record<SupportedLang, Record<string, string>> = {
        de: {
            lessThanXMinutes: "<{count}min",
            xMinutes: "{count}min",
            aboutXHours: "{count}h",
            xHours: "{count}h",
            xDays: "{count}T",
            aboutXDays: "{count}T",
            xMonths: "{count}Mo",
            xYears: "{count}J",
            halfAMinute: "<1min"
        },
        en: {
            lessThanXMinutes: "<{count}m",
            xMinutes: "{count}m",
            aboutXHours: "{count}h",
            xHours: "{count}h",
            xDays: "{count}d",
            aboutXDays: "{count}d",
            xMonths: "{count}mo",
            xYears: "{count}y",
            halfAMinute: "<1m"
        }
    };

    return {
        formatDistance: (token: string, count: number) => {
            const template = map[lang]?.[token] ?? `{count}`;
            return template.replace("{count}", count.toString());
        },
        formatLong: {} as any,
        formatRelative: () => "",
        localize: {} as any,
        match: {} as any,
        options: {}
    };
};

/**
 * Formats the time difference between the given date and now using a compact, language-specific format.
 *
 * Example: "5m" for 5 minutes ago (in English), or "5min" in German.
 *
 * @param date - Date string or Date object to compare with now.
 * @param lang - Language code for formatting (defaults to "de").
 * @returns A short time string like "3h", "2d", etc.
 */

export const formatShortTime = (date?: string | Date, lang: SupportedLang = "de"): string => {
    if (!date) return "";
    return formatDistanceToNow(new Date(date), {
        addSuffix: false,
        locale: getShortTimeLocale(lang)
    });
};
